import _compact from "lodash/compact";
import { CAMPAIGN_ID, MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS, PAYMENT_METHOD, PaymentMethod } from "../../../constants";

const TEXT = {
    COMMON: {
        CONTINUE: "Fortsett",
        OR: "eller",
        LOG_IN: "Logg inn",
        CLOSE: "Lukk",
        SEE_MORE: "Se flere",
        SUBSCRIPTION: "Abonnement",
        YOUR_ORDER: "Din bestilling",
        PER_MONTH: "pr. md.",
        OWNER: "Eier",
        MOBILENUMBER: "Mobilnummer",
        WARNING: "Varsel",
        OBS: "Obs",
        BACK: "Tilbake",
        CANCEL: "Avbryt",
        DELETE: "Slett",
        EDIT: "Endre",
    },
    SWITCH: {
        INFO: "Allerede Telia-kunde?",
        KEEPEXISTING: "Behold eksisterende Telia-abonnement",
    },
    MODAL: {
        EMPTY_CART: {
            HEADING: "Advarsel",
            DESCRIPTION:
                "Du er i ferd med å tømme handlekurven. Hvis du fortsetter blir du sendt tilbake til forsiden av nettbutikken.",
        },
        REMOVE_LEGAL: {
            HEADING: "Advarsel",
            DESCRIPTION:
                "Du er i ferd med å slette abonnementet tilknyttet juridisk eier. Hvis du fortsetter vil handlekurven tømmes.",
        },
        WEBDEAL: {
            HEADING: "Kampanje!",
            DESCRIPTION: "Velg blant følgende med ditt kjøp:",
            SUBMIT: "Velg og fortsett",
            CLOSE: "Nei takk",
        },
        VAS: {
            HEADING: "Vas",
            SUBMIT: "Legg til plussforiskring",
            CLOSE: "Nei takk",
        },
        BAN: {
            BAN_HEADING: "Faktura-ref.:",
            SUBSCRIPTION_HEADING: "Mobilabonnement:",
            MBB_HEADING: "Mobilt Bredbånd:",
        },
        ADDITIONAL_PRODUCTS: {
            EMAIL_IN_USE:
                "E-post adressen må være unik for hver bruker. Velg en annen e-post for å legge til Telia Trygg.",
            ENTER_EMAIL: "Legg inn ny e-post adresse:",
        },
    },
    CONTACT_INFO: {
        HEADING: "Kontaktinformasjon",
        PHONE: "Telefonnummer",
        EMAIL: "E-post",
        UPDATE_DELIVERY_ADDRESS: "Oppdater leveringsadresse",
    },
    BANK_ID: {
        DESCRIPTION_1: "Vi bruker BankID for din sikkerhet.",
        DESCRIPTION_2_CREDIT_CHECK:
            "Ditt fødselsnummer blir kun brukt til å foreta en kredittsjekk, og blir ikke lagret hos oss i etterkant.",
        DESCRIPTION_2_ADDRESS_LOOKUP:
            "Ditt fødselsnummer blir kun brukt til å hente ut adresse fra folkeregisteret, og blir ikke lagret hos oss i etterkant.",
        GRANT_SUCCESS: "Du er nå verifisert med BankID.",
        HEADING: "Bekreft med BankID",
    },
    PIN_BACKUP: {
        HEADING: "Hvem skal være eier og betaler?",
        CONTACT_CARD_HEADING: "Vi har hentet disse opplysningene om deg:",
        DESCRIPTION_1: "Verifiser ditt mobilnummer.",
        PORTIN_WARNING:
            "Dette krever verifisering med BankID, men BankID ser ut til å være nede. Vennligst prøv igjen senere.",
    },
    RESWITCH: {
        CHOOSE_PHONE_MESSAGE: (phone: string, subscription: string | number) =>
            `Velg en ny telefon for å svitsje din ${phone}. Du beholder ditt nåværende abonnement for nummer ${subscription}.`,
    },
    CHOOSE_SUBSCRIPTION: {
        HEADING: "Velg abonnement",
        SHOW_MORE_BUTTON_TEXT: (show: boolean) => (show ? "Se flere abonnement" : "Se færre abonnement"),
    },
    VAS_AND_UPSALES: {
        ACCESSORIES: {
            HEADING: "Anbefalt tilbehør",
        },
        HEADING: "Tilvalg",
        VAS: {
            HEADING: "Anbefalte tjenester",
        },
        COLOR_PICKER: {
            CHOOSE_COLOR: "Velg farge:",
        },
    },
    GROUP: {
        SUBSCRIPTION_CARD_DESCRIPTION:
            "Nå kan du samle flere Telia X abonnement på én regning og spare penger. Alle får ubegrenset data og ubegrenset fart til en lavere månedspris.",
        EXISTING_DISCLAIMER: "*Allerede eksisterende Telia X på denne fakturaen får tilsvarende rabatt",
        PERSONAL_DETAILS_PAGE_CART_TEXT: "Legg til flere abonnement i neste steg og å få lavere pris per abonnement",
    },
    MULTIPLE_SUBSCRIPTIONS: {
        HEADING: "Opplysninger",
        CONTINUE: "Neste",
        COLLAPSED_HEADING: "Hvem skal bruke abonnementet:",
        SUBSCRIPTION_PREFIX: "Telia Mobil ",
        ADD_MEMBER: "Abonnementsdetaljer",
        UPDATE_MEMBER: "Oppdater",
        REGISTERED_MEMBER_TITLE: ({
            isB2BFamilyOffer,
            isExisting,
            isLegal,
            subscriptionName,
        }: {
            isB2BFamilyOffer?: boolean;
            isExisting: boolean;
            isLegal?: boolean;
            subscriptionName?: string;
        }) => {
            if (isB2BFamilyOffer) return "Dekkes av arbeidsgiver";
            if (isLegal) return "Eier og betaler";
            if (isExisting) {
                return subscriptionName
                    ? `Eksisterende ${subscriptionName} abonnement på faktura`
                    : "Eksisterende familieabonnement på faktura";
            }
            return `Abonnement - ${subscriptionName}`;
        },
    },
    PERSONAL_DETAILS: {
        HEADING: (isHardwareOnly: boolean) => (isHardwareOnly ? "Eier og mottaker" : "Eier"),
        PHONENUMBER: {
            HEADING: "Mobilnummer",
            HEADING_MULTIPLE_SUBSCRIPTIONS: "Eier og betaler",
            HEADING_PREPAID: "Eier og betaler",
            INGRESS:
                "Vennligst oppgi ditt eksisterende mobilnummer som vi kan benytte dersom vi trenger å komme i kontakt med deg.",
            INGRESS_FOR_KEEP_EXISTING: "Tast inn ditt Telia mobilnummer",
            KEEP: "Beholde mobilnummer",
            KEEP_INFO: "Enten du vil flytte ditt nummer til oss eller du er Telia kunde i dag.",
            NEW: "Jeg vil ha et nytt mobilnummer",
            NEW_INFO: "(Gjelder for de som ikke har nummer fra før og ønsker å opprette et nå.)",
            CHOOSE: "Velg et nytt mobilnummer",
            CHOOSE_DROPDOWN: "Velg ditt nye nummer",
            YOUR_NUMBER: "Ditt mobilnummer",
            FETCHING_NUMBERS: "Henter tilgjengelige mobilnummer",
        },
        LEGAL_OWNER: {
            HEADING: "Hvem skal være eier og betaler?",
            DESCRIPTION: "Vi tar sikkerhet på alvor. Derfor bruker vi bankID for å bekrefte at du er du.",
            HEADING_BANK_ID_VERIFIED: "Vi hentet disse opplysningene fra BankID",
            MISSING_EMAIL_WARNING:
                "Juridisk eier av abonnement må registrere sin e-postadresse. Telefonnummer er valgfritt.",
            HEADING_PREPAID_TO_POSTPAID: "Husk å bytte SIM-kort!",
            PREPAID_TO_POSTPAID:
                "Denne endringen krever bytte av SIM-kort. Når du du mottar det nye SIM-kortet aktiverer du dette via MinSide eller kundeservice på tlf 924 05 050.",
        },
        PRODUCT_USER: {
            HEADING: "Bruker av abonnement",
        },
        HARDWARE_ONLY: {
            HEADING: "Fyll inn personopplysninger til mottaker av bestillingen",
        },
        VERIFICATION: {
            HEADING: "Bekreft at det er ditt nummer",
            CONFIRM_HEADING: "Verifiser engangskode",
            RESET_BANKID: "Logg ut",
            CANCEL_ORDER: "Avbryt kjøp",
            DESCRIPTION: "Som en ekstra sikkerhet så må du bekrefte at det er ditt nummer",
            WITH_SMS: "Jeg ønsker å verifisere meg med sms",
            WITH_SMS_LABEL: "Skriv inn koden du fikk på sms",
            WITH_EMAIL: "Jeg ønsker å verifisere meg med e-post",
            WITH_EMAIL_LABEL: "Skriv inn koden du fikk på e-post",
            OR_LOG_IN: "eller logg inn på Min Side",
            SEND_SMS: "Send meg SMS-kode", //codeSendLabel
            SEND_EMAIL: "Send meg e-post",
            EMAIL: "e-post",
            SMS: "SMS",
            CODE_VERIFIED_PLACEHOLDER: "XXXX",
            CODE_VERIFIED: "Mobilnummeret er verifisert",
            CAN_NOT_VERIFY_NUMBER: "Kan du ikke verifisere mobilnummeret ditt?",
            SMS_HELP_DIALOG_CONTENT:
                "Er mobilen din mistet, ødelagt eller kan du av andre årsaker ikke motta SMS - ta kontakt med vårt kundesenter på telefon",
            LEGAL_OWNER_CONSENT: {
                HEADING: "Tilleggsgodkjenning kreves",
                DESCRIPTION: "Bekreft at eier godkjenner endringen. Godkjennes ved at eier bekrefter via SMS",
            },
            BANK_ID_MANDATORY_BUT_NOT_AVAILABLE:
                "Dette steget krever verifisering med BankID, men BankID ser ut til å være nede. Vennligst prøv igjen senere.",
            NEW_NUMBER_ABOVE_LIMIT: "Nytt mobilnummer er ikke tilgjengelig ved kjøp av denne telefonen.",
            EXISTING_PREPAID_KBD_ABOVE_LIMIT:
                "Endring fra kontantkort til abonnement kan ikke kombineres med kjøp av telefon. Ønsker du å endre til abonnement uten kjøp av telefon kan du gjøre det <a class='link' href='/mobilabonnement/' target='_blank' rel='noopener noreferrer'>her</a>.",
        },
        CHOOSE_BAN: {
            HEADING: "Vi ser du har flere fakturaer hos oss.",
            DESCRIPTION: (hasOneSubscriptionInCheckout: boolean) =>
                hasOneSubscriptionInCheckout
                    ? "Eller velg en faktura du vil legge abonnementet på"
                    : "Eller velg en faktura du vil legge abonnementene på",
        },
        FIRSTNAME: "Fornavn",
        LASTNAME: "Etternavn",
        STREET: "Gateadresse",
        POSTCODE: "Postnummer",
        CITY: "Poststed",
        SSN: "Fødselsnummer",
        EMAIL: "E-post",
        EMAIL_HELP_TEXT_TELIA_TRYGG: "En aktiveringslenke vil bli sendt til denne e-postadressen",
        BIRTHDATE: "Fødselsdato",
        LEGAL_OWNER_NOT_EQUAL_PRODUCT_USER: "Eier og bruker skal være forskjellige personer",
        NUMBER_SHOULD_NOT_BE_LISTED_IN_CATALOG: "Skjul nummeret hos opplysningstjenester",
        ALLOW_NUMBER_TO_BE_LISTED_IN_CATALOG: "La nummeret være synlig i opplysningstjenestene",
        ACTIVATE_TELIA_TRYGG_LATER: (isEmailTakenForTeliaTrygg?: boolean, isEmailTakenForTeliaSky?: boolean) =>
            `Jeg aktiverer  ${_compact([
                isEmailTakenForTeliaTrygg ? "Telia Trygg" : undefined,
                isEmailTakenForTeliaSky ? "Telia Sky" : undefined,
            ]).join(" og ")} senere på Min Side eller App`,
        SECRET_NUMBER: "Kjøp hemmelig nummer (20,-/md.)",
        TRIAL_PERIOD: {
            HEADING: "Prøv oss i 2 måneder",
            SUBHEADING: "Passer vi ikke får du pengene tilbake",
            LIST: {
                ITEM_1: "Gjelder alle våre mobilabonnement som er kjøpt uten avtaletid.",
                ITEM_2: "Ta kontakt med oss innen 60 dager fra du blir kunde dersom du vil si opp og få tilbake pengene.",
                ITEM_3: "Ekstra tjenester og bruk ut over månedsavgiften vil ikke bli tilbakebetalt.",
                ITEM_4: "Gjelder ikke ved kansellering av nummer, kun dersom du flytter nummeret til en annen mobiloperatør.",
                ITEM_5: "Gjelder kun én gang per kunde.",
                ITEM_6: "Gjelder ikke kontantkort.",
            },
        },
        ERROR_BLACKLISTED:
            "Vi ser at du har prøvd å fullføre en ordre i våre systemer over lengre tid uten å lykkes, og vi ønsker derfor å hjelpe deg med å gjennomføre ordren. Vennligst kontakt vårt kundesenter slik at du får den hjelp du trenger.",
    },
    ADD_MEMBER: {
        HEADING: "Bruker",
        SAME_ADDRESS_CHECKBOX: "Samme adresse som eier/betaler",
        NEXT_USER_BUTTON: "Klar for neste bruker",
        FINAL_USER_BUTTON: "Fortsett",
        FINAL_USER_SUBMIT_ORDER: "Bestill",
        EMAIL_INPUT_DESCRIPTION: "Vi sender deg en e-post med ordrebekreftelsen",
        LEGAL_PHONE_INPUT_DESCRIPTION: "Dette er mobilnummeret som tilknyttes eier av faktura.",
        NEW_LEGEND: "Fyll inn personopplysninger",
        EDIT_LEGEND: (nr: string | number) => `Endre opplysninger for ${nr}`,
        CONTINUE_BUTTON: {
            NEXT_MEMBER: "Neste abonnement",
            SUMMARY: "Se oppsummering",
            SUMMARY_WITH_DELIVERY: "Velg levering",
            COMPLETE: "Fullfør kjøp",
        },
        ADD_EXTRA_SIM: "Velg ekstra SIM",
        E_SIM: {
            TRY_ESIM: "Hvilken type SIM-kort ønsker du?",
            HEADER: "Hva er eSIM?",
            BODY_WITH_HANDSET: {
                ["ESIM-PUSH"]: {
                    DESCRIPTION:
                        "eSIM er et innebygd elektronisk SIM som betyr at du slipper å bytte det fysiske SIM-kort.  *Støtter foreløpig ikke BankID på mobil.",
                },
                ["ESIM"]: {
                    DESCRIPTION:
                        "eSIM er et innebygd elektronisk SIM som betyr at du slipper å bytte det fysiske SIM-kort.  *Støtter foreløpig ikke BankID på mobil.",
                },
                ["ESIM-QR"]: {
                    DESCRIPTION:
                        "eSIM er et innebygd elektronisk SIM som betyr at du slipper å bytte det fysiske SIM-kort. Du installerer det lett med å bruke en QR-kode. Vi sender deg en SMS med lenke til denne installering. *Støtter foreløpig ikke BankID på mobil.",
                },
            },

            BODY_NO_HANDSET:
                "eSIM fungerer akkurat som et tradisjonelt SIM-kort, og kommer innebygd i de fleste nyere telefoner. Hvis din telefon støtter eSIM og du velger dette, så slipper du å vente på å få tilsendt nytt SIM-kort i posten. Vi sender deg istedenfor en e-post hvor du ved hjelp av BankID får tilgang til en QR-kode for installering av eSIMet på telefonen.",
            CHECKBOX: "Jeg vil ha eSIM",
        },
        TELIA_X_SERVICE: {
            UNIQUE_EMAIL: (
                isEmailTakenForTeliaTrygg?: boolean,
                isEmailTakenForTeliaSky?: boolean,
                isTeliaTryggSubscription?: boolean,
            ) => {
                if (isTeliaTryggSubscription) {
                    return "E-post adressen må være unik per bruker av Telia Trygg. Vennligst velg en annen e-post for å legge til.";
                }

                return `Hver bruker må ha en unik e-postadresse i ${_compact([
                    isEmailTakenForTeliaTrygg ? "Telia Trygg" : undefined,
                    isEmailTakenForTeliaSky ? "Telia Sky" : undefined,
                ]).join()}. Velg en annen e-post, eller huk av nedenfor for å aktivere senere via Min Side.`;
            },
        },
        IN_ORDER: "I bestilling",
        BARRINGS_FOR_CHILDREN: "Legg til kostnadskontroll på dette abonnementet",
        BARRINGS_TOOLTIP:
            "Disse sperrene hindrer bruk som koster penger ut over månedsavgiften, slik som ekstra datapakker, bruk utenfor EU, spesialnummer og innholdstjenester. Du kan når som helst endre disse sperrene på MinSide.",
        CAMPAIGN: {
            // Keep one for reference to campaigns in the future
            [CAMPAIGN_ID.VPL_3]: {
                IMG_SRC: "/shop/assets/img-static/campaign/viaplay-logo.svg",
                IMG_ALT: "Viaplay logo",
                DESCRIPTION: (aboName: string) =>
                    `Med ${aboName} kan du få Viaplay Film & Serier uten ekstra kostnad i 3 måneder.`,
                DISCLAIMER:
                    "Etter prøveperioden vil abonnementet løpe videre til 139,- kr/md. Abonnementet kan avsluttes når du vil.",
                OPTION: "Ja, legg til 3 md. Viaplay uten ekstra kostnad.",
                NOT_ALLOWED: "Denne tjenesten har du allerede benyttet tidligere og kan derfor ikke legges til.",
            },
        },
        RADIO_GROUP: {
            LABEL: "Hvem skal bruke abonnementet?",
            MEG_SELV: "Jeg skal bruke det selv",
            NOEN_ANDRE: "Noen andre",
        },
    },
    SUMMARY: {
        HEADING: "Levering",
        HEADING_NO_DELIVERY: "Oppsummering",
        ORDER: "Fullfør kjøp",
        ORDERING: "Fullfører kjøp",
        GO_TO_PAYMENT: "Gå til betaling",
        GOING_TO_PAYMENT: "Går til betaling",
        SHOW_ORDER: "Se over bestillingen min",
        USER_INFO: "Bruker av abonnement",
        RECEIVER: "Bestiller og mottaker",
        YOUR_NUMBER: "Mobilnummer",
        TERMS_AND_CONDITIONS:
            "Ved å fullføre kjøpet aksepterer du <a class='link' href='https://www.telia.no/hjelp/kjopsbetingelser-nettbutikk/' target='_blank' rel='noopener noreferrer'>kjøpsbetingelsene</a>.",
        TERMS_AND_CONDITIONS_WITH_SUBSCRIPTION:
            "Ved å fullføre kjøpet aksepterer du <a class='link' href='https://www.telia.no/hjelp/kjopsbetingelser-nettbutikk/' target='_blank' rel='noopener noreferrer'>kjøpsbetingelsene</a> og <a class='link' href='https://www.telia.no/globalassets/pdf/vilkar/telias-brukervilkar-for-privatpersoner.pdf' target='_blank' rel='noopener noreferrer'>abonnementsvilkår</a>.",
        TERMS_AND_CONDITIONS_LIKE_FIN_INSURANCE:
            "<a class='link' href='https://www.telia.no/globalassets/pdf/forsikring/wtw/telia-vilkar-like-fin-skjerm-og-pluss-forsikring.pdf' target='_blank' rel='noopener noreferrer'>Forsikringsvilkår</a> for Telia LIKE FIN Skjermforsikring og Telia Plussforsikring.",
        TERMS_AND_CONDITIONS_WITH_MOBILPANT:
            "Her finner du <a class='link' href='https://www.telia.no/globalassets/pdf/vilkar/vilkar-for-mobilpant.pdf' target='_blank' rel='noopener noreferrer'>vilkår for Telia Mobilpant</a>.",
        TELIA_FINANCE: {
            TERMS_AND_CONDITIONS:
                "Ved å fullføre kjøpet aksepterer du <a class='link' href='https://www.telia.no/hjelp/kjopsbetingelser-nettbutikk/' target='_blank' rel='noopener noreferrer'>kjøpsbetingelsene</a>.",
            TERMS_AND_CONDITIONS_WITH_SUBSCRIPTION:
                "Ved å fullføre kjøpet aksepterer du <a class='link' href='https://www.telia.no/hjelp/kjopsbetingelser-nettbutikk/' target='_blank' rel='noopener noreferrer'>kjøpsbetingelsene</a> og <a class='link' href='https://www.telia.no/globalassets/pdf/vilkar/telias-brukervilkar-for-privatpersoner.pdf' target='_blank' rel='noopener noreferrer'>abonnementsvilkår</a>.",
        },
        MAX_NUMBER_OF_SUBSCRIPTIONS: {
            HEADING: "Maks antall abonnement nådd",
            DESCRIPTION: `Du har nådd maks antall abonnementer på en konto. Vennligst fjern abonnement fra handlekurven slik at kontoen har maksimalt ${MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS} abonnement totalt`,
        },
        TERMS_AND_COND_FOR_SCREEN_AND_PLUSS_INSURANCE:
            "Les mer om forsikringsvilkår for <a class='link' href='https://www.telia.no/globalassets/pdf/vilkar/vilkar-telia-skjermforsikring-telia-plussforsikring.pdf' target='_blank' rel='noopener noreferrer'>Telia Skjermforsikring og Telia Plussforsikring</a>",
    },
    RECEIPT: {
        HEADING: "Kvittering",
        THANKS: (name: string) => `Takk for bestillingen, ${name}!`,
        EMAIL_CONFIRMATION: "Bekreftelse blir sendt på e-post.",
        TRANSFER_INFORMATION: "Du får en e-post når nåværende eier har godkjent overføringen.",
        DOBBEL_DATA_DISCLAIMER:
            "Obs! På ordrebekreftelsen du får per e-post, vil det kun stå opprinnelig datamengde. Dobbel data legges til og oppdateres når ditt abonnement blir aktivert.",
        TRACKING_SMS: "Du får sporingsnummer tilsendt når varene forlater vårt lager.",
        DELIVERY: "Leveringsmetode/-sted",
        DELIVERY_DATE: "Estimert leveringsdato",
        CONTACT_INFO: "Kontaktinformasjon",
        CONTACT_PHONE: "Telefonnummer",
        CONTACT_EMAIL: "E-postadresse",
        CREDENTIALS: "Husk gyldig legitimasjon ved utlevering av varene.",
        ORDER_BY: "Bestilt av",
        ORDER_DETAILS_HEADING: "Bestillingsdetaljer",
        ORDER_NUMBER: "Ordrenummer",
        COPY_ORDER_ID: "Kopier ordrenummer",
        COPY_ORDER_ID_SUCCESS: "Kopiert!",
        ORDER_TO: "Eier av abonnement",
        ORDER_TYPE: "Bestillingstype",
        UPGRADE: "Oppgradering",
        UPGRADE_TIME: "Umiddelbart",
        YOUR_ORDER: "Din bestilling",
        YOUR_NUMBER: "Ditt mobilnummer",
        TERMS_AND_AGREEMENT: "Vilkår og avtaler",
        SIM_INFORMATION: "Du kan bruke ditt gamle SIM-kort til det nye kommer",
        ACTIVATION_INFORMATION: "Vi oppdaterer deg om aktivering så snart tidspunktet for nummerflytting er klart.",
    },
    TERMS: {
        HEADING: "Vilkår",
        DESCRIPTION_1:
            "Etter inkludert datakvote, og eventuell oppspart data med Data Rollover i våre Telia Mobil-abonnement, reduseres hastigheten til 64 Kbit/s. Etter 100 GB brukt data på Telia X reduseres hastigheten til 3 Mbps, uten at det skal gå ut over opplevelsen på din mobil.",
        DESCRIPTION_2:
            "Med Fri fart er estimert høyeste hastighet for nedlastning 220 Mbps og for opplasting 35 Mbps. De faktiske hastighetene avhenger av kapasitet og andre forhold.",
        DESCRIPTION_3: "Leieavtalen for mobiltelefonen i Svitsjavtalen inngås med Telia Finance.",
        DESCRIPTION_4: "Les mer om hastighet og betydningen av det her",
        DESCRIPTION_5: "Les fullstendige abonnementsvilkår her (PDF)",
        DESCRIPTION_6: "Les fullstendige Telia Norge Svitsj vilkår (PDF)",
        DESCRIPTION_7: "Les fullstendige Telia Finance leievilkår her (PDF)",
        DESCRIPTION_8: "Les fullstendige Svitsj forsikringsvilkår her (PDF)",
        DESCRIPTION_9: "Les fullstendige Telia Skjermforsikring og Telia Plussforsikringsvilkår her (PDF)",
        DESCRIPTION_10: "Les fullstendige Telia LIKE FIN Skjermforsikring og Telia Plussforsikringsvilkår her (PDF)",
        HEADING_MBB_1: "Bruk over inkludert data",
        HEADING_MBB_2: "For abonnementene 50 GB, 100 GB, 250 GB og 500 GB:",
        HEADING_MBB_3: "For abonnementene 10 GB og 25 GB:",
        DESCRIPTION_MBB_1: "Faktiske hastigheter avhenger av kapasitet og andre forhold.",
        DESCRIPTION_MBB_2:
            "Du kan fortsette å surfe etter at du har brukt opp datakvoten i abonnementet, men hastigheten blir noe redusert; 6 Mbps for 50 og 100 GB, 10 Mbps for 250 GB og 500 GB.",
        DESCRIPTION_MBB_3:
            "Etter inkludert datakvote, og eventuell oppspart data med Data Rollover, reduseres hastigheten til 64 kB/s. ",
    },
    WITHDRAWAL_RIGHT: {
        HEADING: "Angrerett",
        DESCRIPTION_1: "Du har 14 dagers angrerett fra du henter varen i butikk ved å benytte deg av angrerettskjema.",
        DESCRIPTION_2:
            "Vi forutsetter at du leverer tilbake produktet i samme stand og mengde som da du fikk dette. Ønsker du å bryte avtaletiden din senere må du dekke et bruddgebyr.",
        DESCRIPTION_3: "Les mer om angrerett her",
    },
    PRICE: {
        MINIMUM_TOTAL_PRICE: "Minste totalpris",
        PER_MONTH: "Å betale pr. md.",
        UPFRONT: "Sum betalt nå",
        UPFRONT_NOW: "Sum å betale nå",
        FIRST_INVOICE: "Belastes på første faktura",
        VOUCHER: "Verdikode",
    },
    AGE_RESTRICTION: {
        SUBSCRIPTION_BARN: "Ved kjøp BARN-abonnement må bruker av abonnementet være under 16 år.",
        SUBSCRIPTION_UNG: "Ved kjøp av UNG-abonnement må bruker av abonnementet være under 30 år.",
    },
    AGE_BENEFITS: {
        YOUNG: {
            HEADING: "Som ung får du fordeler!",
            DESCRIPTION_2: "2 stk. 3-timers gratis Data Boost hver måned",
        },
    },

    PAYMENT: {
        HEADING: "Betaling",
        DESCRIPTION_FOR: (type: PaymentMethod) => {
            switch (type) {
                case PAYMENT_METHOD.NETS:
                case PAYMENT_METHOD.NETS_3D:
                case PAYMENT_METHOD.ADYEN_CC:
                    return "Betale nå med kort";
                case PAYMENT_METHOD.VIPPS:
                case PAYMENT_METHOD.VIPPS_v2:
                    return "Betale nå med Vipps";
                case PAYMENT_METHOD.INVOICE:
                    return "Neste faktura fra Telia";
                default:
                    return "";
            }
        },
        DESCRIPTION_FOR_PAST_TENSE: (type: PaymentMethod) => {
            switch (type) {
                case PAYMENT_METHOD.NETS:
                case PAYMENT_METHOD.NETS_3D:
                    return "Betalt med kort";
                case PAYMENT_METHOD.VIPPS:
                    return "Betalt med Vipps";
                case PAYMENT_METHOD.INVOICE:
                    return "Betales på neste faktura fra Telia";
                default:
                    return "";
            }
        },
    },
    DELIVERY: {
        HEADING: "Levering",
        CLICK_AND_FETCH: {
            HEADING: "Klikk & Hent",
            SUBHEADING: "Hent bestillingen raskt, enkelt og kostnadsfritt i utvalgte Telia‑butikker.",
            DESCRIPTION:
                "Velg en butikk nær deg, og hent bestillingen når det passer deg. Vi gir beskjed på SMS når den er klar for henting. Klikk & Hent er gratis, og bestillingen er klar for henting innen 2 timer etter at du har lagt inn bestillingen.",
        },
        NOTE: (numberOfCharactersLeft: number) => `Kommentar til sjåfør? (${numberOfCharactersLeft} tegn igjen)`,
    },
    SUBSCRIPTION_CHOOSER: {
        HEADING_NO_NAME: "Velg abonnement",
        HEADING_LEGAL: "Velg abonnement for eier",
    },
    ERROR: {
        HEADING: "En feil har oppstått",
        FETCH_PHONE_NUMBER:
            "Beklager, vi kunne ikke hente tilgjengelige numre. Vennligst last siden på nytt for å prøve igjen.",
        POST_ADDRESS_UNAVAILABLE: "Beklager, vi kan ikke sende til postnummeret du har oppgitt.",
        INVALID_PHONE_NUMBER_FORMAT:
            "Mobilnummeret ser ut til å være galt. Har du tastet riktig? Nummeret er 8 siffer langt, starter med 4 eller 9 og skal skrives uten mellomrom.",
        INVALID_PHONE_NUMBER_FORMAT_MBB:
            "Nummeret ser ut til å være galt. Har du tastet riktig? Nummeret er 12 siffer langt, starter med 58 og skal skrives uten mellomrom.",
        ORDER_SUBMIT: "Beklager, noe gikk galt med din bestilling ",
        CHECK_PHONE_NUMBER:
            "Beklager, en feil oppstod ved sjekk av ditt mobilnummer. Prøv igjen, eller ta kontakt med oss på telefon 92405050 for å få hjelp.",
        PAYMENT: "Betalingen din ble ikke gjennomført. Mest sannsynligvis ble den avvist av banken din.",
        CHECK_EMAIL_TELIA_X_SERVICE:
            "Beklager, en feil oppstod ved sjekk av din e-post. Prøv igjen, eller ta kontakt med oss på telefon 92405050 for å få hjelp.",
        NO_SHOP_CHOSEN: "Klikk & Hent er valgt uten å velge en butikk. Vennligst velg en butikk for å gå videre.",
        NO_PICK_UP_POINT_CHOSEN:
            "Post i butikk er valgt uten å velge en butikk. Vennligst velg en butikk for å gå videre.",
        MAX_NUMBER_OF_NEW_NUMBERS_REACHED:
            "Beklager, du har nådd grensen for hvor mange nye mobilnummer man kan ha i en bestilling.",
        UPDATE_PORTIN:
            "Beklager, noe gikk galt ved oppdatering av porteringsdato. Prøv igjen, eller ta kontakt med oss på telefon 92405050 for å få hjelp.",
    },
    GDPR: {
        FILTERTEXT: "[Removed]",
    },
    QUEUE: {
        HEADING: "Kø i Kassa",
    },
} as const;

export default TEXT;
